import React, { useRef } from 'react';
import { Col, Row } from 'antd';
import { I18n } from '@aws-amplify/core';
import { DownOutlined } from '@ant-design/icons';
import 'moment/locale/pt-br';

import { Question, Answer } from '../types';
import * as S from './styles';

interface ModalFinishSurveyProps {
	navigateToQuestion?: (questionId: string) => void;
	reference?: React.ReactNode;
	finishSurvey?: () => void;
	toggleModal: () => void;
	questions?: Question[];
	answers?: Answer[];
	isOpen: boolean;
}

export function ModalFinishSurvey({
	navigateToQuestion,
	questions = [],
	answers = [],
	finishSurvey,
	toggleModal,
	reference,
	isOpen
}: Readonly<ModalFinishSurveyProps>): JSX.Element {
	const hasAllAnswers = questions.length > 0 && questions.length === answers.length;
	const bottomRef = useRef<HTMLDivElement>(null);

	function getFormattedAnswer(questionId: string): string {
		const answer = answers.find((a) => a.question_id === questionId);
		if (!answer) return I18n.get('Not answered');
		if (answer.response) return answer.response;
		if (answer.option_id) {
			const question = questions.find((q) => q.id === questionId);
			const selectedOption = question?.options?.find(
				(opt) => opt.id === answer.option_id || opt.value === answer.option_id
			);
			if (selectedOption) return I18n.get(selectedOption.name);
		}
		return I18n.get('Answered');
	}

	function handleQuestionClick(questionId: string) {
		toggleModal();
		if (navigateToQuestion) navigateToQuestion(questionId);
	}

	function handleFinish() {
		toggleModal();
		if (finishSurvey) finishSurvey();
	}

	function handleScrollToBottom() {
		if (bottomRef.current) {
			bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
		}
	}

	return (
		<>
			<S.SmallModalStyled
				onCancel={toggleModal}
				maskClosable={false}
				closable={false}
				open={isOpen}
				footer={null}
				mask={false}
				centered
			>
				<Row>
					<Col span={24} style={{ position: 'relative' }}>
						{questions.length > 0 ? (
							<>
								<S.SummaryText>
									{I18n.get('Review your answers. Click on any question to edit.')}
								</S.SummaryText>
								<div style={{ position: 'relative' }}>
									<S.QuestionsList>
										{questions.map((question) => (
											<S.SimpleQuestionItem
												key={question.id}
												onClick={() => handleQuestionClick(question.id)}
											>
												<S.QuestionTitle>{I18n.get(question.title)}</S.QuestionTitle>
												{question.title !== question.description && (
													<S.QuestionDescription>
														{I18n.get(question.description)}
													</S.QuestionDescription>
												)}
												<S.AnswerText>{getFormattedAnswer(question.id)}</S.AnswerText>
											</S.SimpleQuestionItem>
										))}
									</S.QuestionsList>
								</div>
								<S.FloatingScrollButton
									onClick={handleScrollToBottom}
									aria-label={I18n.get('Scroll to bottom')}
								>
									<DownOutlined style={{ fontSize: 22 }} />
								</S.FloatingScrollButton>
							</>
						) : (
							<Row justify="center" gutter={[16, 24]}>
								<p>{I18n.get('Your survey is complete!')}</p>
							</Row>
						)}
					</Col>
					{reference}
					<Col span={24} style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
						<S.RoundButton
							disabled={questions.length > 0 && !hasAllAnswers}
							onClick={handleFinish}
							ref={bottomRef}
							type="primary"
						>
							{I18n.get('Finish')}
						</S.RoundButton>
					</Col>
				</Row>
			</S.SmallModalStyled>
		</>
	);
}
